import {
    makeAdUrl,
    makeChatUrl,
    makeExecutorOwnerDetailsUrl,
    makeExecutorUrl,
    makeSellerUrl,
    makeServiceRequesterUrl,
    trans,
} from '@frontend/helpers'
import {
    mdiCheckAll,
    mdiDeleteOutline,
    mdiDotsVertical,
    mdiFileDocumentOutline,
    mdiOpenInNew,
    mdiStarShootingOutline,
} from '@mdi/js'
import {iconProfile} from '@shared/helpers/icons'
import clsx from 'clsx'
import {
    type MouseEvent,
    useRef,
} from 'react'
import {Dropdown} from 'swayok-react-mdb-ui-kit/components/Dropdown/Dropdown'
import {DropdownMenu} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownMenu'
import {DropdownToggle} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownToggle'
import type {DropdownApi} from 'swayok-react-mdb-ui-kit/components/Dropdown/DropdownTypes'
import {Icon} from 'swayok-react-mdb-ui-kit/components/Icon/Icon'
import {NavigationService} from 'swayok-react-mdb-ui-kit/services/NavigationService'
import type {ChatDetailsData} from '../ChatsModuleTypes'
import {ChatActionsDropdownItem} from './ChatActionsDropdownItem'

interface Props {
    chat: ChatDetailsData
    miniChat?: boolean
    disabled?: boolean
    onDelete: (chat: ChatDetailsData) => void
    onRead: (chat: ChatDetailsData) => void
    onAddReview: (chat: ChatDetailsData) => void
    verticalIcon?: boolean
    className?: string
    buttonClassName?: string
    isLast?: boolean
}

// Выпадающее меню действий с чатом.
export function ChatActionsMenu(props: Props) {

    const {
        isLast,
        className = 'd-inline-block',
        buttonClassName = 'p-2 me-n2',
        verticalIcon = false,
        miniChat = false,
        chat,
        onAddReview,
        disabled,
        onRead,
        onDelete,
    } = props

    const dropdownApiRef = useRef<DropdownApi>(null)

    // Открыть URL в новой вкладке или перейти на страницу, если не удалось открыть вкладку.
    const openInNewTab = (url: string) => {
        const tab: Window | null = window.open(url, '_blank')
        if (tab) {
            tab.focus()
        } else {
            NavigationService.navigate(url)
        }
    }

    // В списке чатов есть проблема с тем, что внешний элемент - <a>, и в выпадающем меню
    // тоже используются элементы <a>.
    // Из-за этого в консоли выводится ошибка, что тег <a> не может содержать вложенные теги <a>.
    // Поэтому мы используем DropdownButton вместо DropdownLink.
    return (
        <div className={className}>
            <Dropdown
                focusFirstItemOnOpen={false}
                closeOnScrollOutside
            >
                <DropdownToggle
                    color="icon"
                    className={clsx(
                        'text-muted btn-hover-theme',
                        buttonClassName
                    )}
                    onClick={(event: MouseEvent<HTMLElement>) => {
                        // Отключить стандартную обработку события
                        // и предотвратить передачу события родительскому элементу.
                        // Если этого не сделать, то совершится переход к сообщениям чата.
                        event.preventDefault()
                        event.stopPropagation()
                    }}
                >
                    <Icon
                        path={mdiDotsVertical}
                        rotate={verticalIcon ? undefined : 90}
                        color="muted"
                        className="chats-list-item-menu"
                    />
                </DropdownToggle>
                <DropdownMenu
                    apiRef={dropdownApiRef}
                    drop={isLast ? 'up' : 'down'}
                    align="end"
                    shadow="2-strong"
                    textNowrapOnItems
                >
                    {miniChat && (
                        <ChatActionsDropdownItem
                            title={trans().cabinet.chats.action.open_chat_page}
                            icon={mdiOpenInNew}
                            onClick={() => {
                                NavigationService.navigate(makeChatUrl(chat.id))
                            }}
                        />
                    )}
                    <ChatActionsDropdownItem
                        title={trans().cabinet.chats.action.open_profile}
                        icon={iconProfile}
                        onClick={() => {
                            switch (chat.otherUser.type) {
                                case 'executor':
                                    openInNewTab(makeExecutorOwnerDetailsUrl(chat.otherUser.id))
                                    return
                                case 'service_requester':
                                    openInNewTab(makeServiceRequesterUrl(chat.otherUser.id))
                                    return
                                default:
                                    openInNewTab(makeSellerUrl(chat.otherUser.id))
                                    return
                            }
                        }}
                    />
                    {(chat.canPostReviewAboutSeller || chat.canPostReviewAboutExecutor) && (
                        <ChatActionsDropdownItem
                            title={trans().cabinet.chats.action.add_review}
                            icon={mdiStarShootingOutline}
                            onClick={() => {
                                onAddReview(chat)
                            }}
                        />
                    )}
                    {(!chat.ad?.isDeleted || !chat.executor?.isDeleted) && (
                        <ChatActionsDropdownItem
                            title={trans().cabinet.chats.action.open_advertisement}
                            icon={mdiFileDocumentOutline}
                            onClick={() => {
                                if (chat.ad) {
                                    openInNewTab(
                                        makeAdUrl(
                                            chat.ad.slugWithId,
                                            chat.ad.categorySlugWithId
                                        )
                                    )
                                } else if (chat.executor) {
                                    openInNewTab(
                                        makeExecutorUrl(
                                            chat.executor.slugWithId,
                                            chat.executor.categorySlugWithId
                                        )
                                    )
                                }
                            }}
                        />
                    )}
                    {chat.hasNotViewedMessages && (
                        <ChatActionsDropdownItem
                            title={trans().cabinet.chats.action.read_chat}
                            icon={mdiCheckAll}
                            disabled={disabled}
                            onClick={() => {
                                onRead(chat)
                            }}
                        />
                    )}
                    <ChatActionsDropdownItem
                        title={trans().cabinet.chats.action.delete_chat}
                        icon={mdiDeleteOutline}
                        disabled={disabled}
                        onClick={() => {
                            if (confirm(trans().cabinet.chats.action.delete_chat_confirmation)) {
                                onDelete(chat)
                            }
                        }}
                    />
                </DropdownMenu>
            </Dropdown>
        </div>
    )
}
